import React, { useEffect } from 'react';
import { Plus, MessageCircle } from 'lucide-react';
import { useSupportChat } from '../../contexts/SupportChatContext';
import type { ConversationListItem } from '../../types/conversation.types';

const statusLabels: Record<string, { label: string; color: string }> = {
  active: { label: 'Активный', color: 'var(--info-500)' },
  escalated: { label: 'Обращение создано', color: 'var(--success-500)' },
  closed_by_user: { label: 'Закрыт', color: 'var(--gray-400)' },
  abandoned: { label: 'Неактивен', color: 'var(--gray-400)' },
  resolved_by_agent: { label: 'Решён', color: 'var(--success-500)' },
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffDays === 0) {
    return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays === 1) return 'Вчера';
  if (diffDays < 7) return `${diffDays} дн. назад`;
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

const ConversationList: React.FC = () => {
  const {
    conversations,
    loadConversations,
    conversationsLoading,
    loadConversation,
    startNewConversation,
    conversationId,
  } = useSupportChat();

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  return (
    <div className="flex flex-col-dir flex-1 overflow-hidden">
      <div className="flex-1 overflow-y-auto py-2">
        {conversationsLoading ? (
          <div className="p-4 text-center text-gray-400">
            Загрузка...
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center text-gray-500 text-sm" style={{ padding: 'var(--space-8) var(--space-4)' }}>
            <MessageCircle size={32} className="text-gray-300 mb-3" />
            <p className="m-0">У вас пока нет диалогов</p>
          </div>
        ) : (
          conversations.map((conv: ConversationListItem) => {
            const isActive = conv.id === conversationId;
            const status = statusLabels[conv.status] ?? { label: conv.status, color: 'var(--gray-400)' };

            return (
              <button
                key={conv.id}
                type="button"
                onClick={() => loadConversation(conv.id)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-1)',
                  width: '100%',
                  padding: 'var(--space-3) var(--space-4)',
                  background: isActive ? 'var(--primary-50)' : 'transparent',
                  border: 'none',
                  borderBottom: '1px solid var(--gray-100)',
                  cursor: 'pointer',
                  textAlign: 'left' as const,
                  fontFamily: 'inherit',
                  transition: 'background 0.1s',
                }}
              >
                <div className="flex justify-between items-center">
                  <span className="text-xs font-medium" style={{ color: status.color }}>
                    {status.label}
                  </span>
                  <span className="text-xs text-gray-400">
                    {formatDate(conv.last_activity_at)}
                  </span>
                </div>
                {conv.last_message_preview && (
                  <span className="text-sm text-gray-600 overflow-hidden text-ellipsis whitespace-nowrap">
                    {conv.last_message_preview}
                  </span>
                )}
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <span>{conv.total_messages} сообщ.</span>
                  {conv.support_request_number != null && (
                    <span className="text-success-700 rounded-sm font-medium" style={{ padding: '0 var(--space-1)', background: 'var(--success-50)' }}>
                      Обращение #{conv.support_request_number}
                    </span>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>

      <div className="border-t-gray-200 px-4-py-3">
        <button
          type="button"
          onClick={startNewConversation}
          className="btn-primary w-full flex items-center justify-center gap-2 text-sm"
        >
          <Plus size={16} />
          Новый диалог
        </button>
      </div>
    </div>
  );
};

export default ConversationList;
